import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Shield, Cpu, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../hooks/useBranding";

export default function About() {
  const { t } = useTranslation();
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15)_0%,transparent_50%)] pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{t('about.vision_badge')}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tighter leading-tight font-outfit uppercase">
            {t('about.title')} <br />
            <span className="bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              {t('about.title_highlight')}
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto font-medium leading-relaxed whitespace-pre-line">
            {branding.page_about_content || t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-10 border-white/5 hover:border-indigo-500/30 transition-all group">
              <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-8 border border-indigo-500/20 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('about.transparency_title')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('about.transparency_desc')}
              </p>
            </div>

            <div className="glass-card p-10 border-white/5 hover:border-cyan-500/30 transition-all group">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-8 border border-cyan-500/20 group-hover:scale-110 transition-transform">
                <Cpu className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('about.advanced_ai_title')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('about.advanced_ai_desc')}
              </p>
            </div>

            <div className="glass-card p-10 border-white/5 hover:border-emerald-500/30 transition-all group">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-8 border border-emerald-500/20 group-hover:scale-110 transition-transform">
                <Globe className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('about.global_impact_title')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('about.global_impact_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-white/5 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
                <div>
                    <p className="text-4xl md:text-6xl font-black text-indigo-400 font-outfit mb-2">50K+</p>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{t('about.users_stat')}</p>
                </div>
                <div>
                    <p className="text-4xl md:text-6xl font-black text-cyan-400 font-outfit mb-2">$250M</p>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{t('about.volume_stat')}</p>
                </div>
                <div>
                    <p className="text-4xl md:text-6xl font-black text-emerald-400 font-outfit mb-2">150+</p>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{t('about.countries_stat')}</p>
                </div>
                <div>
                    <p className="text-4xl md:text-6xl font-black text-indigo-400 font-outfit mb-2">24/7</p>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{t('about.support_stat')}</p>
                </div>
            </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
